import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { searchProductsAsync } from '../store/slices/productSlice';
import Loader from '../components/common/loader';
import AddToCartButton from '../components/cart/addToCartButton';
import WishlistButton from '../components/wishlist/wishlistButton';

const SearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { searchResults, loading, error } = useSelector(state => state.products);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('relevance');
  const [filters, setFilters] = useState({
    category: '',
    subCategory: '',
    material: '',
    brand: '',
    minPrice: '',
    maxPrice: ''
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const query = queryParams.get('q') || '';
    const sort = queryParams.get('sort') || 'relevance';
    const category = queryParams.get('category') || '';
    const subCategory = queryParams.get('subCategory') || '';
    const material = queryParams.get('material') || '';
    const brand = queryParams.get('brand') || '';
    const minPrice = queryParams.get('minPrice') || '';
    const maxPrice = queryParams.get('maxPrice') || '';
    
    setSearchTerm(query);
    setSortOption(sort);
    setFilters({
      category,
      subCategory,
      material,
      brand,
      minPrice,
      maxPrice
    });
    
    if (query.length >= 2) {
      const params = {
        limit: 20
      };
      
      if (sort && sort !== 'relevance') {
        const [sortBy, sortOrder] = sort.split('_');
        params.sortBy = sortBy;
        params.sortOrder = sortOrder;
      }
      
      if (category) params.category = category;
      if (subCategory) params.subCategory = subCategory;
      if (material) params.material = material;
      if (brand) params.brand = brand;
      if (minPrice) params.minPrice = minPrice;
      if (maxPrice) params.maxPrice = maxPrice;
      
      dispatch(searchProductsAsync({ query, params }));
    }
  }, [dispatch, location.search]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.length >= 2) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleSortChange = (e) => {
    const newSort = e.target.value;
    setSortOption(newSort);
    
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('sort', newSort);
    navigate(`/search?${queryParams.toString()}`);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    const queryParams = new URLSearchParams(location.search);
    
    // Maintain search query
    const query = queryParams.get('q') || '';
    if (query) queryParams.set('q', query);
    
    // Apply sort
    if (sortOption !== 'relevance') {
      queryParams.set('sort', sortOption);
    } else {
      queryParams.delete('sort');
    }
    
    // Apply filters
    if (filters.category) queryParams.set('category', filters.category);
    else queryParams.delete('category');

    if (filters.subCategory) queryParams.set('subCategory', filters.subCategory);
    else queryParams.delete('subCategory');

    if (filters.material) queryParams.set('material', filters.material);
    else queryParams.delete('material');
    
    if (filters.brand) queryParams.set('brand', filters.brand);
    else queryParams.delete('brand');
    
    if (filters.minPrice) queryParams.set('minPrice', filters.minPrice);
    else queryParams.delete('minPrice');
    
    if (filters.maxPrice) queryParams.set('maxPrice', filters.maxPrice);
    else queryParams.delete('maxPrice');
    
    navigate(`/search?${queryParams.toString()}`);
  };

  const clearFilters = () => {
    setSortOption('relevance');
    setFilters({
      category: '',
      subCategory: '',
      material: '',
      brand: '',
      minPrice: '',
      maxPrice: ''
    });
    
    const queryParams = new URLSearchParams();
    queryParams.set('q', searchTerm);
    navigate(`/search?${queryParams.toString()}`);
  };

  // Calculate discount percentage
  const calculateDiscount = (mrp, price) => {
    if (!mrp || !price || mrp <= price) return 0;
    return Math.round(((mrp - price) / mrp) * 100);
  };

  // Format category/subcategory name
  const formatName = (name) => {
    return name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  // Safe access to search results with defaults
  const products = searchResults?.products || [];
  const query = searchResults?.query || searchTerm;
  const count = searchResults?.count || 0;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumbs */}
      <div className="mb-6">
        <nav className="text-sm">
          <ol className="list-none p-0 inline-flex">
            <li className="flex items-center">
              <Link to="/" className="text-gray-500 hover:text-blue-600">Home</Link>
              <svg className="w-3 h-3 mx-2" fill="currentColor" viewBox="0 0 12 12">
                <path d="M9.707 5.293l-5-5a1 1 0 00-1.414 1.414L7.586 6 3.293 10.293a1 1 0 101.414 1.414l5-5a1 1 0 000-1.414z" />
              </svg>
            </li>
            <li>
              <span className="text-gray-700">Search Results</span>
            </li>
          </ol>
        </nav>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <form onSubmit={handleSearchSubmit} className="flex">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search for mulching film, grow bags, packaging..."
            className="flex-grow border rounded-l px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            minLength={2}
            required
          />
          <button 
            type="submit"
            className="bg-green-600 text-white px-6 py-2 rounded-r hover:bg-green-700 transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        </form>
      </div>

      {/* Main Content */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Filters sidebar */}
        <div className="w-full md:w-1/4 bg-white rounded-lg shadow p-4">
          <h2 className="text-xl font-semibold mb-4">Filters</h2>
          
          <div className="space-y-4">
            {/* Category filter */}
            <div>
              <label className="block text-gray-700 mb-2 font-medium">Category</label>
              <select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">All Categories</option>
                <option value="AGRICULTURE_PRODUCTS">Agriculture Products</option>
                <option value="PACKAGING_PRODUCTS">Packaging Products</option>
              </select>
            </div>

            {/* Sub-Category filter */}
            {filters.category && (
              <div>
                <label className="block text-gray-700 mb-2 font-medium">Sub-Category</label>
                <select 
                  name="subCategory"
                  value={filters.subCategory}
                  onChange={handleFilterChange}
                  className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">All Sub-Categories</option>
                  {filters.category === 'AGRICULTURE_PRODUCTS' && (
                    <>
                      <option value="MULCHING_FILM">Mulching Film</option>
                      <option value="TARPAULINS">Tarpaulins</option>
                      <option value="CROP_COVERS">Crop Covers</option>
                      <option value="NURSERY_FILMS">Nursery Films</option>
                      <option value="NURSERY_SHEETS">Nursery Sheets</option>
                      <option value="IRRIGATION">Irrigation</option>
                      <option value="GROW_BAGS">Grow Bags</option>
                      <option value="SHADE_NET">Shade Net</option>
                      <option value="BIRD_NET">Bird Net</option>
                    </>
                  )}
                  {filters.category === 'PACKAGING_PRODUCTS' && (
                    <>
                      <option value="COURIER_BAGS">Courier Bags</option>
                      <option value="SHRINK_FILM">Shrink Film</option>
                      <option value="PACKAGING_BAGS">Packaging Bags</option>
                      <option value="PAPER_BAGS">Paper Bags</option>
                      <option value="PLASTIC_BAGS">Plastic Bags</option>
                    </>
                  )}
                </select>
              </div>
            )}

            {/* Material filter */}
            <div>
              <label className="block text-gray-700 mb-2 font-medium">Material</label>
              <select 
                name="material"
                value={filters.material}
                onChange={handleFilterChange}
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">All Materials</option>
                <option value="LDPE">LDPE</option>
                <option value="HDPE">HDPE</option>
                <option value="PP">PP</option>
                <option value="PVC">PVC</option>
                <option value="LLDPE">LLDPE</option>
                <option value="KRAFT_PAPER">Kraft Paper</option>
                <option value="BIODEGRADABLE">Biodegradable</option>
                <option value="MESH">Mesh</option>
                <option value="WOVEN">Woven</option>
                <option value="NON_WOVEN">Non-Woven</option>
              </select>
            </div>
            
            {/* Brand filter */}
            <div>
              <label className="block text-gray-700 mb-2 font-medium">Brand</label>
              <input
                type="text"
                name="brand"
                placeholder="Enter brand name"
                value={filters.brand}
                onChange={handleFilterChange}
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            
            {/* Price range */}
            <div>
              <label className="block text-gray-700 mb-2 font-medium">Price Range</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  name="minPrice"
                  placeholder="Min"
                  value={filters.minPrice}
                  onChange={handleFilterChange}
                  className="w-1/2 border rounded p-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <span>-</span>
                <input
                  type="number"
                  name="maxPrice"
                  placeholder="Max"
                  value={filters.maxPrice}
                  onChange={handleFilterChange}
                  className="w-1/2 border rounded p-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
            
            {/* Sort options */}
            <div>
              <label className="block text-gray-700 mb-2 font-medium">Sort By</label>
              <select
                value={sortOption}
                onChange={handleSortChange}
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="relevance">Relevance</option>
                <option value="createdAt_desc">Newest First</option>
                <option value="sellingPrice_asc">Price: Low to High</option>
                <option value="sellingPrice_desc">Price: High to Low</option>
                <option value="discount_desc">Discount: High to Low</option>
              </select>
            </div>
            
            <div className="flex space-x-2 pt-2">
              <button
                onClick={applyFilters}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition w-full"
              >
                Apply
              </button>
              <button
                onClick={clearFilters}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        
        {/* Product grid */}
        <div className="w-full md:w-3/4">
          {searchTerm.length < 2 ? (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-lg">
              <p className="font-medium">Please enter at least 2 characters to search.</p>
            </div>
          ) : loading.search ? (
            <div className="flex justify-center items-center h-64">
              <Loader size="lg" />
            </div>
          ) : error.search ? (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
              <p className="font-medium">
                {typeof error.search === 'string' 
                  ? error.search 
                  : error.search?.message || 'An error occurred while searching'}
              </p>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Search Results for "{query}"
                </h2>
                <span className="text-gray-600 bg-gray-100 px-3 py-1 rounded-full text-sm">
                  {count} result{count !== 1 ? 's' : ''}
                </span>
              </div>
              
              {products.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-8 rounded-lg text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="font-medium text-lg mb-2">No products found</p>
                  <p className="text-sm">Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map(product => (
                    <div key={product.productId} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition">
                      <div className="relative">
                        <Link to={`/product/${product.productId}`}>
                          <div className="h-48 overflow-hidden bg-gray-100">
                            <img 
                              src={product.imageUrl || '/images/placeholder.png'} 
                              alt={product.name} 
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        </Link>

                        {/* Stock Badge */}
                        {product.stockStatus === 'LOW_STOCK' && (
                          <span className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded font-medium">
                            Low Stock
                          </span>
                        )}
                        {product.stockStatus === 'OUT_OF_STOCK' && (
                          <span className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded font-medium">
                            Out of Stock
                          </span>
                        )}

                        {/* Eco-Friendly Badge */}
                        {product.isEcoFriendly && (
                          <span className="absolute top-2 right-10 bg-green-500 text-white text-xs px-2 py-1 rounded font-medium">
                            🌱 Eco
                          </span>
                        )}

                        <div className="absolute top-2 right-2">
                          <WishlistButton productId={product.productId} />
                        </div>
                      </div>
                      
                      <Link to={`/product/${product.productId}`}>
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-900 mb-1 truncate" title={product.name}>
                            {product.name}
                          </h3>
                          
                          {/* Category & SubCategory */}
                          <div className="text-xs text-gray-500 mb-2">
                            <p>{formatName(product.subCategory || product.category)}</p>
                            {product.manufacturer && (
                              <p>By {product.manufacturer}</p>
                            )}
                          </div>

                          {/* Material & Color Tags */}
                          <div className="flex flex-wrap gap-1 mb-2">
                            {product.material && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                {formatName(product.material)}
                              </span>
                            )}
                            {product.color && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                {formatName(product.color)}
                              </span>
                            )}
                          </div>
                          
                          {/* Price */}
                          <div className="flex items-end mb-2">
                            <span className="text-lg font-bold text-gray-900">
                              ₹{product.price}
                            </span>
                            
                            {product.mrp > product.price && (
                              <>
                                <span className="text-sm text-gray-500 line-through ml-2">
                                  ₹{product.mrp}
                                </span>
                                
                                <span className="text-sm text-green-600 ml-2">
                                  {calculateDiscount(product.mrp, product.price)}% off
                                </span>
                              </>
                            )}
                          </div>

                          {/* Bulk Pricing & MOQ */}
                          <div className="space-y-1">
                            {product.hasBulkPricing && (
                              <p className="text-xs text-blue-600">
                                💰 Bulk pricing available
                              </p>
                            )}
                            {product.minOrderQuantity > 1 && (
                              <p className="text-xs text-gray-500">
                                Min Order: {product.minOrderQuantity} units
                              </p>
                            )}
                          </div>
                        </div>
                      </Link>
                      
                      <div className="px-4 pb-4">
                        <AddToCartButton 
                          product={product}
                          fullWidth={true}
                          showQuantity={false}
                          disabled={product.stockStatus === 'OUT_OF_STOCK'}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;